import { DefaultButton, Icon, Stack } from "@fluentui/react";
import { ReactNode } from "react";

import { Contract } from "../../contract.interface";
import { DefaultCardTitle } from "../styles";

import { TranslatedContactType } from "./types";

import { Card } from "@/components/Shared/Card";
import { AccountType } from "@/modules/Accounts/types/Accounts.types";
import { TranslatedMaritalStatus } from "@/modules/Contracts/enums";
import { cpfCnpjFormatter } from "@/utils/FormatCPForCNPJ";
import { formatISOStringToDateString } from "@/utils/FormatISOStringToDateString";
import { formatZipCode } from "@/utils/FormatZipCode";

interface ContractInfoProps {
  openDrawer?: () => void;
  contract: Contract;
  title: string;
  isInsidePanel: boolean;
}

interface ContractDataItemProps {
  title: string;
  value: string | ReactNode;
  "data-clarity-unmask"?: boolean;
}

const tokens = {
  sectionStack: { childrenGap: 10, padding: 20 },
  headingStack: { childrenGap: 10 }
};

export function ClientInfo({
  contract,
  title,
  openDrawer,
  isInsidePanel
}: ContractInfoProps): JSX.Element {
  const { accountAddress } = contract;

  if (!contract.account) {
    return (
      <Stack verticalFill horizontalAlign="center" verticalAlign="center">
        <p>Informações do cooperado não disponíveis</p>
      </Stack>
    );
  }

  return (
    <Stack verticalFill>
      <Stack
        horizontal
        className="ms-sm12"
        tokens={{ childrenGap: 10 }}
        style={{ marginBottom: "1vh", height: 35 }}
      >
        <DefaultCardTitle>
          <Icon iconName="Contact" style={{ marginRight: 10 }} />
          {title}
        </DefaultCardTitle>

        {openDrawer && (
          <DefaultButton id="view-client-button" onClick={openDrawer}>
            Informações
          </DefaultButton>
        )}
      </Stack>

      <Card borderRadius="1rem" padding="1rem">
        <Stack horizontal wrap tokens={tokens.sectionStack}>
          <Stack.Item
            grow
            className={`ms-sm12 ${!isInsidePanel ? "ms-lg5" : ""}`}
          >
            <Stack tokens={tokens.headingStack}>
              <ContractInfoCamp
                title="Nome:"
                value={contract.account.fullName}
              />

              {contract.account.accountType !== AccountType.LEGAL && (
                <>
                  {contract.account.birthDate && (
                    <ContractInfoCamp
                      title="Data de Nascimento:"
                      value={formatISOStringToDateString(
                        contract.account.birthDate
                      )}
                    />
                  )}

                  <ContractInfoCamp
                    title="Estado Civil:"
                    value={String(
                      TranslatedMaritalStatus[
                        contract.account.maritalStatus?.name
                      ]
                    )}
                  />

                  <ContractInfoCamp
                    title="Ocupação:"
                    value={contract.account.occupation}
                  />

                  <ContractInfoCamp
                    title={`${
                      TranslatedContactType[contract.accountContact.contactType]
                    }:`}
                    value={contract.accountContact.contact}
                  />
                </>
              )}
            </Stack>
          </Stack.Item>

          <Stack.Item
            grow
            className={`ms-sm12 ${!isInsidePanel ? "ms-lg5" : ""}`}
          >
            <Stack tokens={tokens.headingStack}>
              {contract.account.secondaryDocument && (
                <ContractInfoCamp
                  key={contract.account.secondaryDocument.id}
                  title={`${contract.account.secondaryDocument.documentType}:`}
                  value={contract.account.secondaryDocument.documentNumber}
                />
              )}

              <ContractInfoCamp
                title={`${contract.accountDocument.documentType}:`}
                value={cpfCnpjFormatter(
                  contract.accountDocument.documentNumber
                )}
              />

              <ContractInfoCamp
                title="Endereço:"
                value={
                  <p>
                    {`${accountAddress.city} - ${accountAddress.state} - CEP ${formatZipCode(accountAddress.zipCode)}`}
                    <br />
                    {`${accountAddress.street}, ${accountAddress.number || ""} ${accountAddress.note || ""} - ${accountAddress.neighborhood}`}
                  </p>
                }
              />

              {contract.checkingAccount && (
                <div style={{ lineHeight: 1.2 }}>
                  <div
                    className="ms-fontWeight-semibold"
                    style={{ width: "280px" }}
                  >
                    Dados Bancários
                  </div>

                  <div>{`Banco ${contract.checkingAccount?.bankNumber}`}</div>
                  <div>{`Agência ${contract.checkingAccount.agencyNumber}`}</div>
                  <div>{`C/C ${contract.checkingAccount.accountNumber}`}</div>
                </div>
              )}
            </Stack>
          </Stack.Item>
        </Stack>
      </Card>
    </Stack>
  );
}

export function ContractInfoCamp({
  title,
  value,
  "data-clarity-unmask": dataClarityUnmask = undefined
}: ContractDataItemProps): JSX.Element {
  return (
    <div
      style={{
        lineHeight: 1.2,
        overflowWrap: "break-word"
      }}
      data-clarity-unmask={dataClarityUnmask}
    >
      <div className="ms-fontWeight-semibold" style={{ width: "280px" }}>
        {title}
      </div>

      <div>{value}</div>
    </div>
  );
}
